/**
 * FIN-7470 (C3DS-09): dados que o provider de autenticação exige e que só o
 * navegador conhece. O bloco é validado por inteiro na borda do BFF — se vier
 * pela metade, o pagamento é recusado antes de chegar ao provider. Por isso
 * toda leitura tem default: ambiente sem alguma API devolve um valor coerente
 * em vez de lançar, e o bloco continua completo.
 *
 * `ip` e `acceptHeader` NÃO entram aqui de propósito: são preenchidos pelo
 * servidor a partir da requisição, e confiar no cliente permitiria forjá-los.
 */
export interface ThreeDsBrowserData {
  userAgent: string;
  language: string;
  colorDepth: number;
  screenWidth: number;
  screenHeight: number;
  timeZoneOffset: number;
  javaEnabled: boolean;
  javascriptEnabled: boolean;
}

/** Profundidade de cor mais comum; usada quando a tela não a informa. */
const DEFAULT_COLOR_DEPTH = 24;
/** Locale padrão do checkout, usado quando o navegador não informa idioma. */
const DEFAULT_LANGUAGE = 'pt-BR';

const toInteger = (value: unknown, fallback: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.trunc(value);
};

const readJavaEnabled = (nav?: Navigator): boolean => {
  try {
    if (typeof nav?.javaEnabled !== 'function') {
      return false;
    }

    return nav.javaEnabled() === true;
  } catch {
    // Navegadores que removeram o plugin de Java podem lançar aqui; o campo é
    // obrigatório no provider, então a ausência vira `false`, não exceção.
    return false;
  }
};

const readTimeZoneOffset = (): number => {
  try {
    return toInteger(new Date().getTimezoneOffset(), 0);
  } catch {
    return 0;
  }
};

export const collectBrowserData = (): ThreeDsBrowserData => {
  if (typeof window === 'undefined') {
    // SSR: nenhuma API de navegador disponível. Devolve o bloco completo com
    // defaults para o chamador não precisar tratar `undefined`.
    return {
      userAgent: '',
      language: DEFAULT_LANGUAGE,
      colorDepth: DEFAULT_COLOR_DEPTH,
      screenWidth: 0,
      screenHeight: 0,
      timeZoneOffset: 0,
      javaEnabled: false,
      javascriptEnabled: true,
    };
  }

  const nav = window.navigator;
  const screen = window.screen;

  return {
    userAgent: typeof nav?.userAgent === 'string' ? nav.userAgent : '',
    language:
      (typeof nav?.language === 'string' && nav.language) ||
      (typeof nav?.languages?.[0] === 'string' && nav.languages[0]) ||
      DEFAULT_LANGUAGE,
    colorDepth: toInteger(screen?.colorDepth, DEFAULT_COLOR_DEPTH),
    screenWidth: toInteger(screen?.width, toInteger(window.innerWidth, 0)),
    screenHeight: toInteger(screen?.height, toInteger(window.innerHeight, 0)),
    timeZoneOffset: readTimeZoneOffset(),
    javaEnabled: readJavaEnabled(nav),
    // Este código só roda com JavaScript habilitado — o campo existe no
    // contrato do provider e é sempre verdadeiro quando somos nós que o
    // preenchemos.
    javascriptEnabled: true,
  };
};
